import { useState, useRef, useEffect } from "react";
import { useWhatsAppOrder } from "@/hooks/useWhatsAppOrder";

const WHATSAPP_NUMBER = "919358208560";

const PRODUCT_OPTIONS = ["Flower Printed Set"];
const SIZE_OPTIONS = ["M", "L", "XL", "XXL"];

export default function WhatsAppOrderModal() {
  const { state, closeModal } = useWhatsAppOrder();
  const { isOpen, defaultProduct, defaultSize } = state;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [productInterest, setProductInterest] = useState(defaultProduct);
  const [size, setSize] = useState(defaultSize);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const overlayRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setProductInterest(defaultProduct);
      setSize(defaultSize);
      setName("");
      setPhone("");
      setCity("");
      setMessage("");
      setErrors({});
      setSubmitting(false);
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isOpen, defaultProduct, defaultSize]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) closeModal();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, closeModal]);

  if (!isOpen) return null;

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required";
    if (!phone.trim()) errs.phone = "Phone is required";
    if (!city.trim()) errs.city = "City is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildWhatsAppMessage = (): string => {
    let text = `Hi NeoRoop! I'm interested in the *${productInterest}*.`;
    text += `\nName: ${name.trim()}`;
    text += `\nCity: ${city.trim()}`;
    if (size) text += `\nSize: ${size}`;
    if (message.trim()) text += `\n${message.trim()}`;
    return text;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    try {
      await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "88c346b5-34bf-479d-8eb6-cf1204cdea5f",
          subject: "New NeoRoop WhatsApp Lead",
          from_name: "NeoRoop Website",
          name: name.trim(),
          phone: phone.trim(),
          city: city.trim(),
          product: productInterest,
          size,
          message: message.trim(),
        }),
      });
    } catch {
      // silently continue to WhatsApp even if form save fails
    }

    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildWhatsAppMessage())}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");

    setSubmitting(false);
    closeModal();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) closeModal();
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors";
  const errorClass = "border-red-400 focus:ring-red-300 focus:border-red-400";
  const labelClass = "text-sm font-medium text-foreground-800";

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
    >
      <div className="bg-background-50 rounded-2xl shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-accent-800">
              Order on WhatsApp
            </h2>
            <p className="text-foreground-500 text-sm mt-1">
              Share a few details and we&rsquo;ll continue on WhatsApp.
            </p>
          </div>
          <button
            onClick={closeModal}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 hover:text-foreground-700 transition-colors flex-shrink-0 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          id="whatsapp-order-form"
          className="px-6 pb-6 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="wa-name" className={labelClass}>
              Name <span className="text-red-400">*</span>
            </label>
            <input
              ref={nameInputRef}
              id="wa-name"
              name="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              className={`${inputClass} ${errors.name ? errorClass : ""}`}
            />
            {errors.name && (
              <span className="text-xs text-red-400">{errors.name}</span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="wa-phone" className={labelClass}>
              Phone <span className="text-red-400">*</span>
            </label>
            <input
              id="wa-phone"
              name="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Your phone number"
              className={`${inputClass} ${errors.phone ? errorClass : ""}`}
            />
            {errors.phone && (
              <span className="text-xs text-red-400">{errors.phone}</span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="wa-city" className={labelClass}>
              City / Location <span className="text-red-400">*</span>
            </label>
            <input
              id="wa-city"
              name="city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Your city"
              className={`${inputClass} ${errors.city ? errorClass : ""}`}
            />
            {errors.city && (
              <span className="text-xs text-red-400">{errors.city}</span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="wa-product" className={labelClass}>
              Product Interest
            </label>
            <select
              id="wa-product"
              name="product"
              value={productInterest}
              onChange={(e) => setProductInterest(e.target.value)}
              className={`${inputClass} cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20d%3D%22M12%2016l-6-6h12z%22%20fill%3D%22%23666%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px] bg-[right_8px_center] bg-no-repeat pr-10`}
            >
              {PRODUCT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="wa-size" className={labelClass}>
              Size
            </label>
            <select
              id="wa-size"
              name="size"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className={`${inputClass} cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20d%3D%22M12%2016l-6-6h12z%22%20fill%3D%22%23666%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px] bg-[right_8px_center] bg-no-repeat pr-10`}
            >
              {SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="wa-message" className={labelClass}>
              Message
            </label>
            <textarea
              id="wa-message"
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Any question or note…"
              className={`${inputClass} resize-none`}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer mt-1"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Continue to WhatsApp
                <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}